import type {
  DictationStatus,
  InstructionDictation,
  UseInstructionDictationOptions,
} from '@/components/session/note-instructions/noteInstructions.types';
import { useRecordingState } from '@/contexts/RecordingContext';
import { SELECTED_MICROPHONE_STORAGE_KEY } from '@/hooks/useAudioDevices';
import { PCMAudioManager } from '@/lib/pcmAudioManager';
import { MicrophoneAccessError, getMicrophoneHelp } from '@careflux/utils/microphone';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { type Socket, io } from 'socket.io-client';

export const CONNECT_ERROR = 'Ekki náðist samband við Skríbuna — reyndu aftur.';
export const UNAVAILABLE_ERROR = 'Raddstýring er ekki í boði fyrir þennan aðgang.';
export const TRANSCRIBE_ERROR = 'Ekki tókst að umrita fyrirmælin — reyndu aftur.';
const MIC_ERROR = 'Ekki tókst að hefja upptöku.';
// Covers a cold socket connect on start, and ASR finalizing on end (the server gives it 10 s).
const ACK_TIMEOUT_MS = 15_000;
// ASR's speech detection has committed everything heard; this grace lets a thinking pause carry on.
export const SETTLE_MS = 1_200;
// Streaming ASR holds about 28 s of uncommitted audio; a longer unbroken instruction is sent at this length.
export const MAX_INSTRUCTION_MS = 25_000;

interface StartAck {
  ok?: boolean;
  reason?: string;
}

interface EndAck {
  text?: string;
}

const micErrorMessage = (error: unknown) =>
  error instanceof MicrophoneAccessError ? getMicrophoneHelp('is').issues[error.issue].title : MIC_ERROR;

/**
 * Hands-free composer instructions over the existing `/stream` socket and ASR streaming.
 *
 * The live text grows while the clinician speaks. A pause ends the instruction: ASR's speech
 * detection has committed everything heard and nothing new follows (or, as a backstop, the
 * capture's silence gate fires); ASR finalizes it and it is applied as heard. Listening pauses
 * while it applies, then resumes until stopped.
 */
export const useInstructionDictation = ({ onInstruction }: UseInstructionDictationOptions): InstructionDictation => {
  const { isActive: isSessionRecording } = useRecordingState();
  const [status, setStatus] = useState<DictationStatus>('idle');
  const [liveText, setLiveText] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Socket and capture callbacks outlive renders, so they read these refs.
  const statusRef = useRef<DictationStatus>('idle');
  const socketRef = useRef<Socket | null>(null);
  const audioRef = useRef<PCMAudioManager | null>(null);
  const heardRef = useRef(false); // the current instruction has sent audio
  const heardSinceRef = useRef(0); // when the current instruction's first audio was sent
  const stopAfterRef = useRef(false); // stop once the instruction in flight settles
  const flushingRef = useRef(false); // pause() is sending the capture's last buffered audio
  const settleTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const updateStatus = (next: DictationStatus) => {
    statusRef.current = next;
    setStatus(next);
  };

  const close = () => {
    const socket = socketRef.current;
    const audio = audioRef.current;
    clearTimeout(settleTimerRef.current);
    socketRef.current = null;
    audioRef.current = null;
    heardRef.current = false;
    stopAfterRef.current = false;
    void audio?.stop();
    socket?.emit('instruction_dictation_stop');
    socket?.disconnect();
    setLiveText('');
    updateStatus('idle');
  };

  const fail = (message: string) => {
    setError(message);
    close();
  };

  const finishInstruction = async () => {
    const socket = socketRef.current;
    const audio = audioRef.current;
    if (!socket || !audio) return;
    clearTimeout(settleTimerRef.current);
    // pause() flushes the last buffered audio through the chunk callback: let it through
    // before leaving 'listening', or the instruction's final words are dropped.
    flushingRef.current = true;
    audio.pause();
    flushingRef.current = false;
    updateStatus('finishing');
    heardRef.current = false;
    let text = '';
    try {
      const ack: EndAck | undefined = await socket.timeout(ACK_TIMEOUT_MS).emitWithAck('instruction_dictation_end');
      text = ack?.text?.trim() ?? '';
    } catch {
      setError(TRANSCRIBE_ERROR);
    }
    if (socketRef.current !== socket) return;
    if (text) {
      setLiveText(text);
      updateStatus('applying');
      await latest.current.onInstruction(text);
      if (socketRef.current !== socket) return;
    }
    if (stopAfterRef.current) {
      close();
      return;
    }
    setLiveText('');
    audio.resetSilenceTracking();
    audio.resume();
    updateStatus('listening');
  };

  const openSocket = () => {
    const socket = io('/stream', { transports: ['websocket'], reconnection: false });
    socket.on('instruction_dictation_text', ({ text, settled }: { text: string; settled?: boolean }) => {
      if (statusRef.current !== 'listening') return;
      setLiveText(text);
      clearTimeout(settleTimerRef.current);
      if (!settled) return;
      settleTimerRef.current = setTimeout(() => {
        if (heardRef.current && statusRef.current === 'listening') void finishInstruction();
      }, SETTLE_MS);
    });
    const lost = () => {
      if (socketRef.current === socket) fail(CONNECT_ERROR);
    };
    socket.on('connect_error', lost);
    socket.on('disconnect', lost);
    return socket;
  };

  const start = async () => {
    if (statusRef.current !== 'idle' || isSessionRecording) return;
    setError(null);
    updateStatus('starting');
    const socket = openSocket();
    socketRef.current = socket;
    try {
      const ack: StartAck | undefined = await socket
        .timeout(ACK_TIMEOUT_MS)
        .emitWithAck('instruction_dictation_start', { language: 'is' });
      if (socketRef.current !== socket) return;
      if (!ack?.ok) {
        fail(ack?.reason === 'unavailable' ? UNAVAILABLE_ERROR : CONNECT_ERROR);
        return;
      }
    } catch {
      if (socketRef.current === socket) fail(CONNECT_ERROR);
      return;
    }

    const audio = new PCMAudioManager();
    audio.onSilenceStateChange((isSilent) => {
      if (isSilent && heardRef.current && statusRef.current === 'listening') void finishInstruction();
    });
    try {
      const deviceId = localStorage.getItem(SELECTED_MICROPHONE_STORAGE_KEY) || undefined;
      await audio.start(deviceId, (chunk) => {
        if (statusRef.current !== 'listening') return;
        if (!heardRef.current) heardSinceRef.current = Date.now();
        heardRef.current = true;
        socket.emit('instruction_dictation_audio', chunk.pcmArrayBuffer, chunk.sampleRate);
        if (!flushingRef.current && Date.now() - heardSinceRef.current >= MAX_INSTRUCTION_MS) {
          void finishInstruction();
        }
      });
    } catch (startError) {
      void audio.stop();
      if (socketRef.current === socket) fail(micErrorMessage(startError));
      return;
    }
    // Stopped or unmounted while the mic was opening.
    if (socketRef.current !== socket) {
      void audio.stop();
      return;
    }
    audioRef.current = audio;
    updateStatus('listening');
  };

  const stop = () => {
    const current = statusRef.current;
    if (current === 'listening' && heardRef.current) {
      stopAfterRef.current = true;
      void finishInstruction();
    } else if (current === 'finishing' || current === 'applying') {
      stopAfterRef.current = true;
    } else if (current !== 'idle') {
      close();
    }
  };

  const latest = useRef({ onInstruction, stop, close });
  useLayoutEffect(() => {
    latest.current = { onInstruction, stop, close };
  });

  // A session recording that starts mid-dictation ends it, so the two never share the mic.
  useEffect(() => {
    if (isSessionRecording) latest.current.stop();
  }, [isSessionRecording]);

  useEffect(() => () => latest.current.close(), []);

  return {
    status,
    liveText,
    isBlocked: isSessionRecording,
    error,
    start,
    stop,
    clearError: () => setError(null),
  };
};
